import {
  AMAP_CHARGING_DESTINATION,
  AMAP_CHARGING_VEHICLE_START,
  FALLBACK_CHARGING_STATIONS,
  type ChargingStation,
  type GeoPoint,
  rankChargingStations,
  updateChargingStationRouteFacts
} from "../config/amapChargingDemo";
import {
  createFallbackCarwashShops,
  estimateCarwashRouteMinutes,
  getCarwashDisplayName,
  rankCarwashShops,
  type CarwashShop
} from "../config/amapCarwashDemo";
import {
  createFallbackFuelStations,
  estimateFuelRouteMinutes,
  rankFuelStations,
  type FuelStation
} from "../config/amapFuelDemo";
import {
  createFallbackParcelPickupPoints,
  estimateParcelRouteMinutes,
  formatParcelEta,
  getApproxDistanceM,
  rankParcelPickupPoints,
  type ParcelPickupPoint
} from "../config/amapParcelDemo";
import type { TraditionalDesktopDestinationTarget } from "../components/cabin/TraditionalDesktopMapDestination";

export type VehicleLocationStatus = "locating" | "live" | "demo" | "failed";

export interface VehicleLocationState {
  vehicleLocation: GeoPoint;
  locationStatus: VehicleLocationStatus;
  locationSource: "amap" | "browser" | "demo";
  locationAccuracyM?: number;
  locationUpdatedAt?: number;
  locationError?: string;
}

export interface RouteGeometry {
  activePath: GeoPoint[];
  nextPath?: GeoPoint[];
  source: "amap" | "fallback";
  status: "loading" | "ready" | "fallback";
}

interface AmapPoi {
  id?: string;
  name?: string;
  address?: string;
  location?: string;
  distance?: string | number;
  type?: string;
}

interface AmapAroundSearchResponse {
  status?: string;
  info?: string;
  pois?: AmapPoi[];
}

export interface ResolvedDestinationLocation {
  name: string;
  location: GeoPoint;
  source: "amap" | "demo";
}

interface AmapDrivingStep {
  polyline?: string;
}

interface AmapDrivingPath {
  distance?: string | number;
  duration?: string | number;
  steps?: AmapDrivingStep[];
}

interface AmapDrivingResponse {
  status?: string;
  info?: string;
  route?: {
    paths?: AmapDrivingPath[];
  };
}

interface CachedRoutePath {
  cachedAt: number;
  path: GeoPoint[];
}

let amapScriptPromise: Promise<any> | null = null;
const DRIVING_ROUTE_CACHE_PREFIX = "geely-amap-driving-route-v1:";
const DRIVING_ROUTE_CACHE_TTL_MS = 1000 * 60 * 60 * 12;
const drivingRouteMemoryCache = new Map<string, CachedRoutePath>();

const FALLBACK_DESTINATION_LOCATIONS: Array<{
  aliases: string[];
  location: GeoPoint;
}> = [
  {
    aliases: ["虹桥火车站", "上海虹桥火车站", "上海虹桥站", "虹桥站"],
    location: { lng: 121.32785, lat: 31.20063 }
  },
  {
    aliases: ["上海迪士尼", "迪士尼", "迪士尼乐园", "上海迪士尼乐园"],
    location: { lng: 121.66795, lat: 31.14974 }
  },
  {
    aliases: ["前滩太古里", "上海前滩太古里"],
    location: { lng: 121.48039, lat: 31.15307 }
  },
  {
    aliases: ["陆家嘴中心", "陆家嘴", "陆家嘴中心l+mall"],
    location: AMAP_CHARGING_DESTINATION.location
  }
];

export function getDemoVehicleLocationState(): VehicleLocationState {
  return {
    vehicleLocation: AMAP_CHARGING_VEHICLE_START.location,
    locationStatus: "demo",
    locationSource: "demo"
  };
}

export function getFallbackDestinationLocation(destinationName: string): ResolvedDestinationLocation {
  const normalizedName = normalizeDestinationLookupName(destinationName);
  const match = FALLBACK_DESTINATION_LOCATIONS.find((candidate) =>
    candidate.aliases.some((alias) => normalizedName.includes(normalizeDestinationLookupName(alias)))
  );

  return {
    name: destinationName.trim() || AMAP_CHARGING_DESTINATION.name,
    location: match?.location ?? AMAP_CHARGING_DESTINATION.location,
    source: "demo"
  };
}

export async function resolveDestinationLocation(
  webServiceKey: string,
  destinationName: string
): Promise<ResolvedDestinationLocation> {
  const fallbackDestination = getFallbackDestinationLocation(destinationName);
  const trimmedName = destinationName.trim();

  if (!trimmedName) {
    return fallbackDestination;
  }

  const params = new URLSearchParams({
    key: webServiceKey,
    keywords: trimmedName,
    city: "上海",
    citylimit: "false",
    offset: "1",
    page: "1",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/place/text?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-destination-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapAroundSearchResponse;
  const poi = payload.pois?.[0];
  const location = parseAmapPoint(poi?.location);

  if (payload.status !== "1" || !location) {
    throw new Error(payload.info || "amap-destination-empty-result");
  }

  return {
    name: poi?.name?.trim() || trimmedName,
    location,
    source: "amap"
  };
}

export async function fetchNearbyChargingStations(
  webServiceKey: string,
  destinationLocation: GeoPoint = AMAP_CHARGING_DESTINATION.location,
  vehicleLocation: GeoPoint = AMAP_CHARGING_VEHICLE_START.location
) {
  const params = new URLSearchParams({
    key: webServiceKey,
    location: `${destinationLocation.lng},${destinationLocation.lat}`,
    keywords: "充电站",
    types: "011100",
    radius: "3000",
    offset: "8",
    page: "1",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/place/around?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapAroundSearchResponse;

  if (payload.status !== "1" || !payload.pois?.length) {
    throw new Error(payload.info || "amap-empty-result");
  }

  const normalizedStations = payload.pois
    .map((poi, index) => normalizeAmapPoi(poi, index, vehicleLocation, destinationLocation))
    .filter((station): station is ChargingStation => Boolean(station))
    .sort((a, b) => a.distanceM - b.distanceM);

  if (!normalizedStations.length) {
    throw new Error("amap-no-charging-station");
  }

  return rankChargingStations(normalizedStations).slice(0, 3);
}

export async function fetchNearbyParcelPickupPoints(
  webServiceKey: string,
  vehicleLocation: GeoPoint,
  pendingCount = 2
) {
  const params = new URLSearchParams({
    key: webServiceKey,
    location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
    keywords: "菜鸟驿站|快递驿站|丰巢|快递柜|驿站",
    radius: "3000",
    offset: "10",
    page: "1",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/place/around?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-parcel-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapAroundSearchResponse;

  if (payload.status !== "1" || !payload.pois?.length) {
    throw new Error(payload.info || "amap-parcel-empty-result");
  }

  const normalizedPoints = payload.pois
    .map((poi, index) => normalizeAmapParcelPoi(poi, index, vehicleLocation, pendingCount))
    .filter((point): point is ParcelPickupPoint => Boolean(point))
    .sort((a, b) => a.distanceM - b.distanceM);

  if (!normalizedPoints.length) {
    throw new Error("amap-no-parcel-pickup-point");
  }

  return rankParcelPickupPoints(normalizedPoints).slice(0, 1);
}

export async function fetchNearbyCarwashShops(
  webServiceKey: string,
  vehicleLocation: GeoPoint
) {
  const params = new URLSearchParams({
    key: webServiceKey,
    location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
    keywords: "洗车|汽车美容|精洗|自助洗车",
    radius: "5000",
    offset: "12",
    page: "1",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/place/around?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-carwash-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapAroundSearchResponse;

  if (payload.status !== "1" || !payload.pois?.length) {
    throw new Error(payload.info || "amap-carwash-empty-result");
  }

  const normalizedShops = payload.pois
    .map((poi, index) => normalizeAmapCarwashPoi(poi, index, vehicleLocation))
    .filter((shop): shop is CarwashShop => Boolean(shop))
    .sort((a, b) => a.distanceM - b.distanceM);

  if (!normalizedShops.length) {
    throw new Error("amap-no-carwash-shop");
  }

  return rankCarwashShops(normalizedShops).slice(0, 3);
}

export async function fetchNearbyFuelStations(
  webServiceKey: string,
  vehicleLocation: GeoPoint
) {
  const params = new URLSearchParams({
    key: webServiceKey,
    location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
    keywords: "加油站|中石化|中石油|壳牌",
    types: "010100",
    radius: "5000",
    offset: "12",
    page: "1",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/place/around?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-fuel-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapAroundSearchResponse;

  if (payload.status !== "1" || !payload.pois?.length) {
    throw new Error(payload.info || "amap-fuel-empty-result");
  }

  const normalizedStations = payload.pois
    .map((poi, index) => normalizeAmapFuelPoi(poi, index, vehicleLocation))
    .filter((station): station is FuelStation => Boolean(station))
    .sort((a, b) => a.distanceM - b.distanceM);

  if (!normalizedStations.length) {
    throw new Error("amap-no-fuel-station");
  }

  return rankFuelStations(normalizedStations).slice(0, 3);
}

export async function fetchRouteGeometry({
  destinationLocation,
  selectedStation,
  target,
  vehicleLocation,
  webServiceKey
}: {
  destinationLocation: GeoPoint;
  selectedStation: ChargingStation;
  target: TraditionalDesktopDestinationTarget;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  if (target === "charging") {
    const [activePath, nextPath] = await Promise.all([
      getCachedDrivingRoutePath(webServiceKey, vehicleLocation, selectedStation.location),
      getCachedDrivingRoutePath(webServiceKey, selectedStation.location, destinationLocation)
    ]);

    return {
      activePath,
      nextPath,
      source: "amap",
      status: "ready"
    };
  }

  return {
    activePath: await getCachedDrivingRoutePath(webServiceKey, vehicleLocation, destinationLocation),
    source: "amap",
    status: "ready"
  };
}

export async function fetchParcelRouteGeometry({
  selectedPickupPoint,
  vehicleLocation,
  webServiceKey
}: {
  selectedPickupPoint: ParcelPickupPoint;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  return {
    activePath: await getCachedDrivingRoutePath(webServiceKey, vehicleLocation, selectedPickupPoint.location),
    source: "amap",
    status: "ready"
  };
}

export async function fetchCarwashRouteGeometry({
  selectedCarwashShop,
  vehicleLocation,
  webServiceKey
}: {
  selectedCarwashShop: CarwashShop;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  return {
    activePath: await getCachedDrivingRoutePath(webServiceKey, vehicleLocation, selectedCarwashShop.location),
    source: "amap",
    status: "ready"
  };
}

export async function fetchFuelRouteGeometry({
  selectedFuelStation,
  vehicleLocation,
  webServiceKey
}: {
  selectedFuelStation: FuelStation;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  return {
    activePath: await getCachedDrivingRoutePath(webServiceKey, vehicleLocation, selectedFuelStation.location),
    source: "amap",
    status: "ready"
  };
}

export function createFallbackRouteGeometry(
  destinationLocation: GeoPoint,
  selectedStation: ChargingStation,
  target: TraditionalDesktopDestinationTarget,
  vehicleLocation: GeoPoint
): RouteGeometry {
  const activePath = target === "charging"
    ? [
        vehicleLocation,
        getRouteBendPoint(selectedStation.location, vehicleLocation),
        selectedStation.location
      ]
    : [
        vehicleLocation,
        getDirectRouteBendPoint(vehicleLocation, destinationLocation),
        destinationLocation
      ];

  return {
    activePath,
    nextPath: target === "charging" ? [selectedStation.location, destinationLocation] : undefined,
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackParcelRouteGeometry(
  selectedPickupPoint: ParcelPickupPoint,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getParcelRouteBendPoint(selectedPickupPoint.location, vehicleLocation),
      selectedPickupPoint.location
    ],
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackCarwashRouteGeometry(
  selectedCarwashShop: CarwashShop,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getCarwashRouteBendPoint(selectedCarwashShop.location, vehicleLocation),
      selectedCarwashShop.location
    ],
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackFuelRouteGeometry(
  selectedFuelStation: FuelStation,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getFuelRouteBendPoint(selectedFuelStation.location, vehicleLocation),
      selectedFuelStation.location
    ],
    source: "fallback",
    status: "fallback"
  };
}

export async function resolveCurrentVehicleLocation(): Promise<VehicleLocationState> {
  let amapError: unknown = null;

  try {
    await loadAmapScript();
    const amapLocation = await getAmapCurrentVehicleLocation();

    return {
      vehicleLocation: amapLocation.location,
      locationStatus: "live",
      locationSource: "amap",
      locationAccuracyM: amapLocation.accuracyM,
      locationUpdatedAt: Date.now()
    };
  } catch (error) {
    amapError = error;
  }

  try {
    const browserLocation = await getBrowserCurrentVehicleLocation();
    const convertedLocation = await convertGpsLocationForAmap(browserLocation.location);

    return {
      vehicleLocation: convertedLocation,
      locationStatus: "live",
      locationSource: "browser",
      locationAccuracyM: browserLocation.accuracyM,
      locationUpdatedAt: Date.now()
    };
  } catch (browserError) {
    throw new Error(getLocationErrorMessage(amapError ?? browserError));
  }
}

export function loadAmapScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window-unavailable"));
  }

  if (window.AMap) {
    return Promise.resolve(window.AMap);
  }

  if (amapScriptPromise) {
    return amapScriptPromise;
  }

  const jsKey = import.meta.env.VITE_AMAP_JS_KEY;
  const securityJsCode = import.meta.env.VITE_AMAP_SECURITY_JS_CODE;

  if (!jsKey || !securityJsCode) {
    return Promise.reject(new Error("missing-amap-js-key"));
  }

  window._AMapSecurityConfig = {
    securityJsCode
  };

  amapScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>("script[data-geely-amap-js='true']");

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        if (window.AMap) {
          resolve(window.AMap);
          return;
        }

        reject(new Error("amap-script-loaded-without-amap"));
      }, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("amap-script-error")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(jsKey)}`;
    script.async = true;
    script.dataset.geelyAmapJs = "true";
    script.addEventListener("load", () => {
      if (window.AMap) {
        resolve(window.AMap);
        return;
      }

      reject(new Error("amap-script-loaded-without-amap"));
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("amap-script-error")), { once: true });
    document.head.appendChild(script);
  });

  return amapScriptPromise;
}

async function getCachedDrivingRoutePath(webServiceKey: string, origin: GeoPoint, destination: GeoPoint) {
  const cacheKey = getDrivingRouteCacheKey(origin, destination);
  const cachedRoute = readDrivingRouteCache(cacheKey);

  if (cachedRoute) {
    return cachedRoute.path;
  }

  const path = await fetchDrivingRoutePath(webServiceKey, origin, destination);
  writeDrivingRouteCache(cacheKey, path);
  return path;
}

async function fetchDrivingRoutePath(webServiceKey: string, origin: GeoPoint, destination: GeoPoint) {
  const params = new URLSearchParams({
    key: webServiceKey,
    origin: formatLngLat(origin),
    destination: formatLngLat(destination),
    strategy: "0",
    extensions: "base",
    output: "JSON"
  });
  const response = await fetch(`https://restapi.amap.com/v3/direction/driving?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`amap-driving-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapDrivingResponse;
  const steps = payload.route?.paths?.[0]?.steps ?? [];
  const routePath = normalizeRoutePath(steps.flatMap((step) => parseAmapLocation(step.polyline)));

  if (payload.status !== "1" || routePath.length < 2) {
    throw new Error(payload.info || "amap-driving-empty-route");
  }

  return routePath;
}

function readDrivingRouteCache(cacheKey: string) {
  const memoryRoute = drivingRouteMemoryCache.get(cacheKey);

  if (isUsableCachedRoute(memoryRoute)) {
    return memoryRoute;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cachedText = window.localStorage.getItem(cacheKey);

    if (!cachedText) {
      return null;
    }

    const cachedRoute = JSON.parse(cachedText) as CachedRoutePath;

    if (!isUsableCachedRoute(cachedRoute)) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    drivingRouteMemoryCache.set(cacheKey, cachedRoute);
    return cachedRoute;
  } catch {
    return null;
  }
}

function writeDrivingRouteCache(cacheKey: string, path: GeoPoint[]) {
  const cachedRoute: CachedRoutePath = {
    cachedAt: Date.now(),
    path
  };

  drivingRouteMemoryCache.set(cacheKey, cachedRoute);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(cachedRoute));
  } catch {
    // Route cache is an optimization only.
  }
}

function isUsableCachedRoute(cachedRoute: CachedRoutePath | undefined | null): cachedRoute is CachedRoutePath {
  return Boolean(
    cachedRoute &&
      Array.isArray(cachedRoute.path) &&
      cachedRoute.path.length >= 2 &&
      Date.now() - cachedRoute.cachedAt < DRIVING_ROUTE_CACHE_TTL_MS
  );
}

function getDrivingRouteCacheKey(origin: GeoPoint, destination: GeoPoint) {
  return `${DRIVING_ROUTE_CACHE_PREFIX}${formatCachePoint(origin)}>${formatCachePoint(destination)}`;
}

function formatCachePoint(point: GeoPoint) {
  return `${point.lng.toFixed(5)},${point.lat.toFixed(5)}`;
}

function formatLngLat(point: GeoPoint) {
  return `${point.lng},${point.lat}`;
}

function parseAmapLocation(location: string | undefined) {
  if (!location) {
    return [];
  }

  return location
    .split(";")
    .map(parseAmapPoint)
    .filter((point): point is GeoPoint => Boolean(point));
}

function parseAmapPoint(location: string | undefined): GeoPoint | null {
  if (!location) {
    return null;
  }

  const [lngText, latText] = location.split(",");
  const lng = Number(lngText);
  const lat = Number(latText);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return { lng, lat };
}

function normalizeRoutePath(points: GeoPoint[]) {
  const normalizedPoints: GeoPoint[] = [];

  points.forEach((point) => {
    const previousPoint = normalizedPoints[normalizedPoints.length - 1];

    if (!previousPoint || previousPoint.lng !== point.lng || previousPoint.lat !== point.lat) {
      normalizedPoints.push(point);
    }
  });

  if (normalizedPoints.length <= 520) {
    return normalizedPoints;
  }

  const step = Math.ceil(normalizedPoints.length / 520);
  return normalizedPoints.filter((_, index) => index === 0 || index === normalizedPoints.length - 1 || index % step === 0);
}

function normalizeAmapPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint,
  destinationLocation: GeoPoint
): ChargingStation | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || FALLBACK_CHARGING_STATIONS[index]?.name || "附近充电站";
  const searchableText = `${name}${poi.type ?? ""}`;

  if (!/(充电|超充|快充|能源|电站)/.test(searchableText)) {
    return null;
  }

  const fallback = FALLBACK_CHARGING_STATIONS[index] ?? FALLBACK_CHARGING_STATIONS[FALLBACK_CHARGING_STATIONS.length - 1];
  const distanceM = Number(poi.distance ?? fallback.distanceM);

  return updateChargingStationRouteFacts({
    ...fallback,
    id: poi.id || `amap-station-${index}`,
    name,
    shortName: shortenStationName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: Number.isFinite(distanceM) ? distanceM : fallback.distanceM,
    source: "amap",
    tag: undefined
  }, vehicleLocation, destinationLocation);
}

function normalizeAmapParcelPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint,
  pendingCount: number
): ParcelPickupPoint | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近快递驿站";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(快递|驿站|菜鸟|丰巢|取件|代收|智能柜|快运)/.test(searchableText)) {
    return null;
  }

  const fallbackPoints = createFallbackParcelPickupPoints(vehicleLocation, pendingCount);
  const fallback = fallbackPoints[index] ?? fallbackPoints[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(80, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.3, normalizedDistanceM / 1000 * 1.18);
  const routeMinutes = estimateParcelRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-parcel-${index}`,
    name,
    shortName: shortenParcelPointName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: normalizedDistanceM,
    pendingCount,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.2, Math.min(1.2, routeDistanceKm * 0.42)),
    eta: formatParcelEta(routeMinutes),
    source: "amap",
    tag: undefined
  };
}

function normalizeAmapCarwashPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint
): CarwashShop | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近洗车门店";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(洗车|汽车美容|精洗|自助洗|车管家|车享|养护|汽车服务)/.test(searchableText)) {
    return null;
  }

  const fallbackShops = createFallbackCarwashShops(vehicleLocation);
  const fallback = fallbackShops[index] ?? fallbackShops[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(100, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.6, normalizedDistanceM / 1000 * 1.22);
  const routeMinutes = estimateCarwashRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-carwash-${index}`,
    name,
    shortName: shortenCarwashShopName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: normalizedDistanceM,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.3, Math.min(1.8, routeDistanceKm * 0.36)),
    source: "amap",
    tag: undefined
  };
}

function normalizeAmapFuelPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint
): FuelStation | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近加油站";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(加油|中石化|中石油|中国石化|中国石油|壳牌|能源站|油站)/.test(searchableText)) {
    return null;
  }

  const fallbackStations = createFallbackFuelStations(vehicleLocation);
  const fallback = fallbackStations[index] ?? fallbackStations[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(120, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.7, normalizedDistanceM / 1000 * 1.18);
  const routeMinutes = estimateFuelRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-fuel-${index}`,
    name,
    shortName: shortenFuelStationName(name),
    address: poi.address?.trim() || fallback.address,
    brandLabel: inferFuelBrandLabel(name, fallback.brandLabel),
    location,
    distanceM: normalizedDistanceM,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.3, Math.min(1.8, routeDistanceKm * 0.36)),
    source: "amap",
    tag: undefined
  };
}

function shortenParcelPointName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/快递代收点$/, "代收点")
    .slice(0, 12);
}

function shortenCarwashShopName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/汽车美容.*$/, "汽车美容")
    .replace(/洗车服务.*$/, "洗车")
    .trim()
    .slice(0, 12);
}

function shortenFuelStationName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/中国石油/, "中石油")
    .replace(/中国石化/, "中石化")
    .replace(/加油站.*$/, "站")
    .trim()
    .slice(0, 12);
}

function inferFuelBrandLabel(name: string, fallbackBrand: string) {
  if (/中石化|中国石化/.test(name)) {
    return "中石化";
  }

  if (/中石油|中国石油/.test(name)) {
    return "中石油";
  }

  if (/壳牌/.test(name)) {
    return "壳牌";
  }

  return fallbackBrand;
}

async function getAmapCurrentVehicleLocation() {
  const AMap = window.AMap;

  if (!AMap?.plugin) {
    throw new Error("amap-geolocation-unavailable");
  }

  await new Promise<void>((resolve, reject) => {
    AMap.plugin("AMap.Geolocation", () => {
      if (AMap.Geolocation) {
        resolve();
        return;
      }

      reject(new Error("amap-geolocation-plugin-missing"));
    });
  });

  return new Promise<{ location: GeoPoint; accuracyM?: number }>((resolve, reject) => {
    const geolocation = new AMap.Geolocation({
      enableHighAccuracy: true,
      timeout: 9000,
      needAddress: false,
      extensions: "base",
      convert: true,
      noIpLocate: 0,
      noGeoLocation: 0
    });

    geolocation.getCurrentPosition((status: string, result: any) => {
      const position = result?.position;

      if (status === "complete" && position) {
        const lng = typeof position.getLng === "function" ? position.getLng() : position.lng;
        const lat = typeof position.getLat === "function" ? position.getLat() : position.lat;
        const accuracyM = Number(result?.accuracy);

        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          resolve({
            location: { lng, lat },
            accuracyM: Number.isFinite(accuracyM) ? accuracyM : undefined
          });
          return;
        }
      }

      reject(new Error(result?.message || result?.info || "amap-geolocation-failed"));
    });
  });
}

function getBrowserCurrentVehicleLocation() {
  return new Promise<{ location: GeoPoint; accuracyM?: number }>((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("browser-geolocation-unavailable"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          location: {
            lng: position.coords.longitude,
            lat: position.coords.latitude
          },
          accuracyM: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : undefined
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 9000
      }
    );
  });
}

async function convertGpsLocationForAmap(location: GeoPoint) {
  const AMap = window.AMap;

  if (!AMap?.convertFrom) {
    return location;
  }

  return new Promise<GeoPoint>((resolve) => {
    AMap.convertFrom([location.lng, location.lat], "gps", (status: string, result: any) => {
      const convertedLocation = result?.locations?.[0];

      if (status === "complete" && convertedLocation) {
        resolve({
          lng: typeof convertedLocation.getLng === "function" ? convertedLocation.getLng() : convertedLocation.lng,
          lat: typeof convertedLocation.getLat === "function" ? convertedLocation.getLat() : convertedLocation.lat
        });
        return;
      }

      resolve(location);
    });
  });
}

function getLocationErrorMessage(error: unknown) {
  const geolocationError = error as { code?: number } | null;

  if (typeof geolocationError?.code === "number") {
    if (geolocationError.code === 1) {
      return "location-permission-denied";
    }

    if (geolocationError.code === 2) {
      return "location-position-unavailable";
    }

    if (geolocationError.code === 3) {
      return "location-timeout";
    }
  }

  return error instanceof Error ? error.message : "location-failed";
}

function getDirectRouteBendPoint(vehicleLocation: GeoPoint, destinationLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + destinationLocation.lng) / 2 - 0.001,
    lat: (vehicleLocation.lat + destinationLocation.lat) / 2 + 0.0012
  };
}

function normalizeDestinationLookupName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[+\-·.]/g, "")
    .replace(/上海市?/g, "")
    .replace(/火车站/g, "站");
}

function getRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 - 0.0011,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.0015
  };
}

function getParcelRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 + 0.0008,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.001
  };
}

function getCarwashRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 - 0.0006,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.0018
  };
}

function getFuelRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 + 0.0012,
    lat: (vehicleLocation.lat + point.lat) / 2 - 0.001
  };
}

function shortenStationName(name: string) {
  return name
    .replace(/[（(].*?[）)]/g, "")
    .replace(/上海市?/g, "")
    .replace(/有限公司/g, "")
    .trim();
}
